/**
 * Comp `.convos` — every conversation in scope (owner 2026-10-31: search, groups, and time sections).
 *
 * Without a search: the pinned conversations, then the person's groups (each opens on its own conversations), then
 * every conversation in no group under time sections ("Recent" for the last three days, then week by week, a busy week
 * day by day: `conversation-time-sections.ts`). With a search: the matching topics across every group, in the same
 * time sections. Rows are `ConversationRailRow`. The search box is the rail's, at its top (owner 2026-11-01).
 */
import type React from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessagesSquare, Search } from 'lucide-react';
import { Button } from '../../components/ui/Button.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { listConversationGroupsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { Conversation } from '../../generated/api/types.gen.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useSimpleConversations } from '../../hooks/useSimpleConversations.js';
import { PAGE_HELP } from '../../help/index.js';
import { useLiveConversationIds } from './simpleTurnActivity.js';
import { ConversationGroupSection } from './ConversationGroupSection.js';
import { ConversationRailRow } from './ConversationRailRow.js';
import { conversationTimeSections } from './conversation-time-sections.js';

interface Props {
  activeModuleId: string;
  /** What the rail's search box holds; empty lists every conversation. */
  search: string;
  selectedConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
}

const SECTION_LABEL = 'px-2 pb-1 pt-3 font-mono text-[9.5px] uppercase tracking-[1px] text-faint';

export const SimpleConversationList: React.FC<Props> = ({
  activeModuleId,
  search,
  selectedConversationId,
  onSelectConversation,
}) => {
  const term = useDebouncedValue(search.trim(), 250);
  const searching = term.length > 0;

  const ungrouped = useSimpleConversations(activeModuleId, { groupId: 'none', enabled: !searching });
  const found = useSimpleConversations(activeModuleId, { search: term, enabled: searching });
  const groups = useQuery({ ...listConversationGroupsOptions(), enabled: !searching });
  const liveIds = useLiveConversationIds();
  const showTags = activeModuleId === 'all';
  const help = PAGE_HELP['simple-landing'];

  const list = searching ? found : ungrouped;
  const rows = list.data ?? [];
  const pinned = searching ? [] : rows.filter((conversation) => conversation.pinnedAt != null);
  const timed = searching ? rows : rows.filter((conversation) => conversation.pinnedAt == null);
  const sections = conversationTimeSections(timed, (conversation) => new Date(conversation.lastActivityAt));
  const shownGroups = searching ? [] : (groups.data?.data ?? []).filter((group) => group.conversationCount > 0);

  const row = (conversation: Conversation) => (
    <ConversationRailRow
      key={String(conversation.id)}
      conversation={conversation}
      active={String(conversation.id) === selectedConversationId}
      live={liveIds.has(String(conversation.id))}
      showTag={showTags}
      onSelect={onSelectConversation}
    />
  );

  const nothingYet = !searching && list.isSuccess && rows.length === 0 && shownGroups.length === 0;

  return (
    <>
      <div className="px-2 pb-1.5 pt-4 font-mono text-[10px] uppercase tracking-[1.2px] text-faint">Conversations</div>

      <div className="flex min-h-[130px] flex-1 flex-col gap-0.5 overflow-y-auto">
        {list.isError ? (
          <Button variant="ghost" size="sm" className="justify-start text-xs" onClick={() => void list.refetch()}>
            Could not load conversations. Retry
          </Button>
        ) : nothingYet ? (
          <EmptyState
            icon={MessagesSquare}
            title={help?.emptyState.title ?? 'Nothing here yet'}
            description={help?.emptyState.description ?? ''}
          />
        ) : searching && list.isSuccess && rows.length === 0 ? (
          <EmptyState icon={Search} title="No conversation matches" description={`No topic contains "${term}".`} />
        ) : (
          <>
            {pinned.length > 0 && (
              <section aria-label="Pinned" className="flex flex-col gap-0.5">
                <h3 className={SECTION_LABEL}>Pinned</h3>
                {pinned.map(row)}
              </section>
            )}
            {shownGroups.length > 0 && (
              <section aria-label="Groups" className="flex flex-col gap-0.5">
                <h3 className={SECTION_LABEL}>Groups</h3>
                {shownGroups.map((group) => (
                  <ConversationGroupSection
                    key={group.id}
                    group={group}
                    activeModuleId={activeModuleId}
                    selectedConversationId={selectedConversationId}
                    liveIds={liveIds}
                    showTags={showTags}
                    onSelectConversation={onSelectConversation}
                  />
                ))}
              </section>
            )}
            {groups.isError && !searching && (
              <Button variant="ghost" size="sm" className="justify-start text-xs" onClick={() => void groups.refetch()}>
                Could not load your groups. Retry
              </Button>
            )}
            {sections.map((section) => (
              <section key={section.key} aria-label={section.label} className="flex flex-col gap-0.5">
                <h3 className={SECTION_LABEL}>{section.label}</h3>
                {section.rows.map(row)}
              </section>
            ))}
            {list.hasOlder && (
              <Button
                variant="ghost"
                size="sm"
                className="justify-start text-xs text-muted"
                loading={list.loadingOlder}
                onClick={list.showOlder}
              >
                Show older conversations
              </Button>
            )}
          </>
        )}
      </div>
    </>
  );
};
